import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { TagChip } from './GuestTagChips';
import {
  GUEST_PRIMARY_BUTTON_SX,
  GUEST_SECONDARY_BUTTON_SX,
  GUEST_TEXT_BUTTON_SX,
  TRACKER_EYEBROW_SX,
} from './guestTableStyles';
import {
  AUTO_TAG_KIND,
  TAG_KIND_LABELS,
  TAG_KINDS,
  assignmentSentence,
  canCreateTag,
  groupTagsByKind,
  isHealthKind,
  planTagAssignment,
  searchTags,
  tagCounts,
  tagLabelProblem,
  type CreateTagKind,
  type TagAssignMode,
} from '../../../lib/guestTags';
import type { GuestTag, TrackerGuest } from '../../../services/tracker-api-client';

export interface TagApply {
  tagIds: string[];
  mode: TagAssignMode;
  /** Only ever sent for a critical tag; '' otherwise. */
  note: string;
}

export interface TagPaletteProps {
  /** What the host is tagging. One guest is a batch of one — there is no other path. */
  guests: TrackerGuest[];
  vocabulary: GuestTag[];
  /** Every guest on the trip, so the live counts are trip-wide, not filter-wide. */
  allGuests: TrackerGuest[];
  busy: boolean;
  /** Non-empty when the last apply was refused; shown above the buttons. */
  error: string;
  /**
   * What the Create dropdown starts on, which is the INTENT the palette was
   * opened with. "Record a requirement" opens on a health kind, so a typed
   * "Sesame allergy" lands in Health & access rather than in the social well
   * the button did not offer. Everything else opens on Auto.
   */
  defaultKind?: CreateTagKind;
  onApply: (apply: TagApply) => void;
  onCreate: (label: string, kind: CreateTagKind) => void;
  onClose: () => void;
}

const MODE_LABELS: Readonly<Record<TagAssignMode, string>> = {
  add: 'Add to these guests',
  remove: 'Remove from these guests',
};

/**
 * Picking tags and putting them on a set of guests.
 *
 * One component, three callers — the bulk bar, the single-guest record, and the
 * seating side panel — because "which tags go on these people" is one question
 * and answering it in three places is how three different rules appear.
 *
 * Two things here are worth stating because they are invisible once it works:
 *
 *  - **Counts are trip-wide.** The chip beside "Vegetarian" says how many people
 *    on the whole roster are vegetarian, not how many of the twelve on screen.
 *    A caterer's question is about the trip, and a count that silently changed
 *    with the filter would be the wrong number to read out loud.
 *  - **The sentence above the button is rendered from the plan that goes on the
 *    wire.** `planTagAssignment` computes what the apply will do — including how
 *    many of the guests it actually changes — and this only renders it. A host
 *    has to be able to read that before the button rather than discover it
 *    after, and one function producing both is what keeps the two the same.
 *
 * There are two modes, add and remove. A `replace` was cut: even scoped to the
 * kinds supplied it cleared dietary tags the host never named, and the tag most
 * likely to be standing alone on somebody is an allergy. Setting one guest's
 * tags is remove then add.
 */
export default function TagPalette({
  guests,
  vocabulary,
  allGuests,
  busy,
  error,
  defaultKind = AUTO_TAG_KIND,
  onApply,
  onCreate,
  onClose,
}: TagPaletteProps) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<TagAssignMode>('add');
  const [note, setNote] = useState('');
  const [newKind, setNewKind] = useState<CreateTagKind>(defaultKind);
  const searchRef = useRef<HTMLInputElement>(null);

  const counts = useMemo(() => tagCounts(allGuests), [allGuests]);
  const matches = useMemo(() => searchTags(vocabulary, query), [vocabulary, query]);
  const grouped = useMemo(() => groupTagsByKind(matches), [matches]);
  const offerCreate = canCreateTag(vocabulary, query);
  const createProblem = query.trim() ? tagLabelProblem(query, vocabulary) : '';

  const plan = useMemo(
    () => planTagAssignment(mode, picked, guests, vocabulary),
    [mode, picked, guests, vocabulary],
  );

  // A critical tag is the only one whose per-assignment note is worth typing —
  // "EpiPen in her bag" is the fact, "college friends" has no such fact — so the
  // field appears for those and stays out of the way otherwise.
  const notable = picked.some((id) => vocabulary.find((tag) => tag.id === id)?.isCritical);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const toggle = (tagId: string) =>
    setPicked((previous) =>
      previous.includes(tagId) ? previous.filter((id) => id !== tagId) : [...previous, tagId],
    );

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        {guests.length === 1
          ? `Tags for ${guests[0].fullName}`
          : `Tag ${guests.length} ${guests.length === 1 ? 'guest' : 'guests'}`}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          placeholder="Search tags, or type a new one"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            // Enter on an exact single match toggles it, so a host who knows the
            // name never has to reach for the mouse. Enter with nothing matching
            // creates, which is the other half of typing a name that isn't there.
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (matches.length === 1) toggle(matches[0].id);
            else if (offerCreate && !createProblem) onCreate(query.trim(), newKind);
          }}
          inputProps={{ 'aria-label': 'Search tags' }}
        />

        {vocabulary.length === 0 ? (
          <Typography sx={{ mt: 2, color: 'var(--tsb-fg-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
            No tags on this trip yet. Type a name above — “College friends”, “Vegetarian”,
            “Wheelchair” — and create it. Whatever you type is the tag; nothing has to match a list.
          </Typography>
        ) : null}

        {grouped.map((group) => (
          <Box key={group.kind} sx={{ mt: 2 }}>
            <Typography component="h3" sx={{ ...TRACKER_EYEBROW_SX, color: 'var(--tsb-fg-muted)' }}>
              {group.label}
              {isHealthKind(group.kind) ? ' — host only, never shown to guests' : ''}
            </Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.75, mt: 1 }}>
              {group.tags.map((tag) => {
                const on = picked.includes(tag.id);
                return (
                  <Box
                    key={tag.id}
                    component="button"
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(tag.id)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      p: 0.5,
                      border: '1px solid',
                      borderColor: on ? 'var(--tsb-brand-border)' : 'transparent',
                      borderRadius: 'var(--tsb-radius-pill)',
                      bgcolor: on ? 'var(--tsb-brand-tint)' : 'transparent',
                      cursor: 'pointer',
                      '&:focus-visible': { boxShadow: 'var(--tsb-focus-ring)', outline: 'none' },
                    }}
                  >
                    <TagChip tag={tag} />
                    <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5, pr: 0.5 }}>
                      {counts[tag.id] ?? 0}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}

        {query.trim() && matches.length === 0 && !offerCreate ? (
          <Typography sx={{ mt: 2, color: 'var(--tsb-fg-muted)', fontSize: 12.5 }}>
            {createProblem || `Nothing matches “${query.trim()}”.`}
          </Typography>
        ) : null}

        {offerCreate ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            sx={{ mt: 2 }}
          >
            <TextField
              select
              size="small"
              label="Kind"
              value={newKind}
              onChange={(event) => setNewKind(event.target.value as CreateTagKind)}
              /*
                Auto is the empty string, and MUI renders no display for an
                empty value unless told to — so without these the host saw a
                blank box under an un-shrunk label, and "Auto — from the name"
                existed only inside the open menu. A box that looks empty is
                what makes somebody pick a kind, which is exactly the override
                that sends the seeded vocabulary's own slugs to the wrong kind.
              */
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            >
              {/*
                Auto sends no kind at all, which is what lets the server's
                seeded vocabulary decide: "Wheelchair" becomes an access tag
                already marked critical, "Rugby lot" a group. A browser default
                of 'group' overrules that silently — and on a seeded slug it
                overrules it into a 400 about a critical flag the host never
                touched.
              */}
              <MenuItem value={AUTO_TAG_KIND}>Auto — from the name</MenuItem>
              {TAG_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {TAG_KIND_LABELS[kind]}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              disabled={busy || Boolean(createProblem)}
              onClick={() => onCreate(query.trim(), newKind)}
              sx={GUEST_SECONDARY_BUTTON_SX}
            >
              {`Create “${query.trim()}”`}
            </Button>
            {createProblem ? (
              <Typography sx={{ color: 'var(--tsb-warning-ink)', fontSize: 12 }}>
                {createProblem}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {picked.length > 0 ? (
          <Box sx={{ mt: 2.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="What to do"
              value={mode}
              onChange={(event) => setMode(event.target.value as TagAssignMode)}
            >
              {(Object.keys(MODE_LABELS) as TagAssignMode[]).map((option) => (
                <MenuItem key={option} value={option}>
                  {MODE_LABELS[option]}
                </MenuItem>
              ))}
            </TextField>
            {notable && mode !== 'remove' ? (
              <TextField
                fullWidth
                size="small"
                label="Detail for the kitchen or the server"
                placeholder="EpiPen in her bag — tell the server"
                value={note}
                onChange={(event) => setNote(event.target.value.slice(0, 200))}
                helperText="Host only. Saved against this tag on these guests."
                sx={{ mt: 1.5 }}
              />
            ) : null}
            <Typography sx={{ mt: 1.5, color: 'var(--tsb-fg-body)', fontSize: 12.5, lineHeight: 1.6 }}>
              {assignmentSentence(plan, vocabulary)}
            </Typography>
          </Box>
        ) : null}

        {plan.refusal && picked.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {plan.refusal}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          disabled={busy}
          onClick={onClose}
          sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          disabled={busy || picked.length === 0 || Boolean(plan.refusal)}
          onClick={() =>
            onApply({
              tagIds: plan.tagIds,
              mode,
              // A remove writes no assignment, so it has nothing to hang a note
              // on — sending one would be a value the server must discard.
              note: notable && mode !== 'remove' ? note.trim() : '',
            })
          }
          sx={GUEST_PRIMARY_BUTTON_SX}
        >
          {busy ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
